import * as Astronomy from "astronomy-engine";

export const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

export const SIGN_TRAITS = {
  Aries: "Fast",
  Taurus: "Anchor",
  Gemini: "Signal",
  Cancer: "Tide",
  Leo: "Radiance",
  Virgo: "Precision",
  Libra: "Balance",
  Scorpio: "Depth",
  Sagittarius: "Range",
  Capricorn: "Frame",
  Aquarius: "Vector",
  Pisces: "Merge",
};

const norm = (d) => ((d % 360) + 360) % 360;

function tzOffsetMs(tz, utcDate) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(utcDate).map((x) => [x.type, x.value]));
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUTC - utcDate.getTime();
}

export function zonedTimeToUtc(dateStr, timeStr, tz) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const naive = Date.UTC(y, m - 1, d, hh, mm);
  let offset = tzOffsetMs(tz, new Date(naive));
  offset = tzOffsetMs(tz, new Date(naive - offset));
  return new Date(naive - offset);
}

export function dayOfYear(y, m, d) {
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 0)) / 86400000);
}

export function computeChart({ date, time, tz, lat, lon, timeUnknown }) {
  const t = zonedTimeToUtc(date, timeUnknown ? "12:00" : time, tz);
  const sunLon = norm(Astronomy.SunPosition(t).elon);
  const moonLon = norm(Astronomy.EclipticGeoMoon(t).lon);
  const gast = Astronomy.SiderealTime(t);
  const lstDeg = norm(gast * 15 + lon);
  const ramc = (lstDeg * Math.PI) / 180;
  const eps = (23.4367 * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const ascRad = Math.atan2(Math.cos(ramc), -(Math.sin(ramc) * Math.cos(eps) + Math.tan(latRad) * Math.sin(eps)));
  const asc = norm((ascRad * 180) / Math.PI);
  const sign = (l) => SIGNS[Math.floor(l / 30)];
  const deg = (l) => (l % 30).toFixed(1);
  return {
    sun: { sign: sign(sunLon), deg: deg(sunLon) },
    moon: { sign: sign(moonLon), deg: deg(moonLon) },
    rising: timeUnknown
      ? { sign: sign(sunLon), deg: null }
      : { sign: sign(asc), deg: deg(asc) },
  };
}

export function fnv1a(seed) {
  let h = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(seed)) {
    h = ((h ^ BigInt(byte)) * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  const hex = h.toString(16).toUpperCase().padStart(16, "0");
  return hex.match(/.{4}/g).join("-");
}

export function sourceId({ name, date, time, timeUnknown, lat, lon }) {
  const [y, m, d] = date.split("-").map(Number);
  let birthMinutes = -1;
  if (!timeUnknown) {
    const [hh, mm] = time.split(":").map(Number);
    birthMinutes = hh * 60 + mm;
  }
  const now = new Date();
  const seed = [
    y,
    dayOfYear(y, m, d),
    birthMinutes,
    Math.round(lat * 100),
    Math.round(lon * 100),
    now.getFullYear(),
    dayOfYear(now.getFullYear(), now.getMonth() + 1, now.getDate()),
    name.toLowerCase().trim(),
  ].join("|");
  return fnv1a(seed);
}
